// Utilities for generating the RPRS Excel template and parsing an uploaded template.
// Requires dependency: `xlsx` (SheetJS) -> npm i xlsx

import * as XLSX from "xlsx";

export const TEMPLATE_SHEET = "Template";
export const INSTRUCTIONS_SHEET = "Instructions";
export const DATA_SHEET = "Data";

export const TEMPLATE_HEADERS = [
  "CLIENTCODE",
  "SERVINDEX",
  "NEW_CP",
  "NEW_CM",
  "NEW_SP",
  "NEW_SM",
];

export function normalizeClientCode(v) {
  if (v == null) return "";
  let s = String(v).trim();
  // Handles Excel text preservation patterns, e.g. ="04916-001"
  if (s.startsWith("=") && s.includes('"')) {
    s = s.replace(/^=\s*"/, "").replace(/"\s*$/, "");
  }
  s = s.replace(/^\s*\=\s*"?/, "").replace(/"\s*$/, "");
  return s.trim();
}

export function normalizeServindex(v) {
  if (v == null) return "";
  const s = String(v).trim();
  if (!s) return "";
  if (s.toUpperCase() === "NA") return "NA";
  return s.toUpperCase();
}

// PE client codes are letters, digits, "-" and "." (a few legacy codes, e.g. 03130.888A).
// The active_services lookup splices codes into a SQL IN list, so nothing else gets through.
export function isSafeClientCode(v) {
  return /^[A-Za-z0-9.-]+$/.test(String(v || ""));
}

export function normalizeHeader(h) {
  return String(h || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_")
    .replace(/[^A-Z0-9_]/g, "");
}

function csvParse(text) {
  const rows = [];
  let row = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];
    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') inQuotes = false;
      else cur += ch;
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ",") { row.push(cur); cur = ""; }
      else if (ch === "\n") {
        row.push(cur.replace(/\r$/, ""));
        rows.push(row);
        row = [];
        cur = "";
      } else cur += ch;
    }
  }
  row.push(cur);
  rows.push(row);
  return rows.filter(r => r.some(c => String(c).trim() !== ""));
}

function normalizeRowKeys(obj) {
  const out = { ...obj };
  if (out.CLIENT_CODE && !out.CLIENTCODE) out.CLIENTCODE = out.CLIENT_CODE;
  if (out.CLIENT && !out.CLIENTCODE) out.CLIENTCODE = out.CLIENT;
  if (out.SERVICE && !out.SERVINDEX) out.SERVINDEX = out.SERVICE;
  if (out.SERV_INDEX && !out.SERVINDEX) out.SERVINDEX = out.SERV_INDEX;
  if (out.NEW_CLIENT_PARTNER && !out.NEW_CP) out.NEW_CP = out.NEW_CLIENT_PARTNER;
  if (out.NEW_CLIENT_MANAGER && !out.NEW_CM) out.NEW_CM = out.NEW_CLIENT_MANAGER;
  if (out.NEW_SERVICE_PARTNER && !out.NEW_SP) out.NEW_SP = out.NEW_SERVICE_PARTNER;
  if (out.NEW_SERVICE_MANAGER && !out.NEW_SM) out.NEW_SM = out.NEW_SERVICE_MANAGER;
  return out;
}

export async function parseRprsImportFile(file) {
  const name = String(file?.name || "").toLowerCase();
  const isCsv = name.endsWith(".csv") || file?.type === "text/csv";

  if (isCsv) {
    const text = await file.text();
    const aoa = csvParse(text);
    if (!aoa.length) return { rows: [] };
    const header = aoa[0].map(normalizeHeader);
    const rows = aoa.slice(1).map((cells) => {
      const obj = {};
      header.forEach((h, i) => (obj[h] = cells[i] ?? ""));
      return obj;
    });
    return { rows: rows.map(normalizeRowKeys) };
  }

  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array", cellDates: true, cellText: false });
  const sheetName = wb.Sheets[TEMPLATE_SHEET] ? TEMPLATE_SHEET : wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  if (!ws) return { rows: [] };

  const raw = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
  const rows = raw.map((r) => {
    const obj = {};
    for (const [k, v] of Object.entries(r)) obj[normalizeHeader(k)] = v;
    return normalizeRowKeys(obj);
  });

  return { rows };
}

export function buildRprsTemplateWorkbook({ employees = [], clients = [] } = {}) {
  const wb = XLSX.utils.book_new();

  const instructionsAoa = [
    ["RPRS Bulk Reassignment Import"],
    [""],
    ["How to use:"],
    ["1) Fill out the Template sheet. One row per CLIENTCODE + SERVINDEX context."],
    ["2) A row can change CP/CM/SP/SM. Leave any NEW_* blank to ignore."],
    ["3) For CP/CM-only rows, SERVINDEX can be NA or blank."],
    ["4) For SP/SM changes, SERVINDEX is required (e.g., ACCTG, BUSTAX)."],
    ["5) NEW_* fields must match employee full name exactly (use Data sheet to copy/paste)."],
    [""],
    // ["Notes:"],
    // ["- CLIENTCODE column is formatted as Text to preserve leading zeros."],
    // ["- Data sheet is reference only; the system still validates on import."],
  ];
  const wsInstr = XLSX.utils.aoa_to_sheet(instructionsAoa);
  wsInstr["!cols"] = [{ wch: 80 }];
  XLSX.utils.book_append_sheet(wb, wsInstr, INSTRUCTIONS_SHEET);

  // const templateAoa = [TEMPLATE_HEADERS, ["=\"04916-001\"", "ACCTG", "", "", "", ""]];
  const templateAoa = [TEMPLATE_HEADERS, ["04916-001", "ACCTG", "Daniel Eison", "Daniel Eison", "Tyler Crawford", "Tyler Crawford"]];
  const wsTemplate = XLSX.utils.aoa_to_sheet(templateAoa);
  wsTemplate["!cols"] = [
    { wch: 14 }, { wch: 10 },
    { wch: 26 }, { wch: 26 }, { wch: 26 }, { wch: 26 },
  ];
  for (let r = 2; r <= templateAoa.length; r++) {
    const addr = `A${r}`;
    if (wsTemplate[addr]) wsTemplate[addr].z = "@";
  }
  XLSX.utils.book_append_sheet(wb, wsTemplate, TEMPLATE_SHEET);

  const empHeader = ["STAFFNAME", "STAFFINDEX", "STAFFUSER"];
  const empRows = employees
    .map((e) => [e.staffName, e.staffIndex, e.staffUser])
    .filter((r) => r[0] && r[1] != null);

  const clientHeader = ["CLIENTCODE", "CLIENTNAME", "SERVINDEX"];
  const clientRows = clients
    .map((c) => [c.clientCode, c.clientName, c.servindex])
    .filter((r) => r[0]);

  const dataAoa = [
    ["EMPLOYEES"],
    empHeader,
    ...empRows,
    [],
    ["CLIENTS_SERVICES"],
    clientHeader,
    ...clientRows,
  ];
  const wsData = XLSX.utils.aoa_to_sheet(dataAoa);
  wsData["!cols"] = [{ wch: 30 }, { wch: 12 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(wb, wsData, DATA_SHEET);

  // Hide Data sheet by default
  wb.Workbook = wb.Workbook || {};
  wb.Workbook.Sheets = wb.SheetNames.map((n) => ({ name: n, Hidden: n === DATA_SHEET ? 1 : 0 }));
  return wb;
}

export function downloadWorkbook(wb, filename) {
  XLSX.writeFile(wb, filename);
}

export async function runPool(items, concurrency, worker) {
  const results = [];
  let i = 0;
  async function runOne() {
    while (i < items.length) {
      const idx = i++;
      try {
        const value = await worker(items[idx], idx);
        results[idx] = { ok: true, value };
      } catch (err) {
        results[idx] = { ok: false, error: err };
      }
    }
  }
  const runners = Array.from({ length: Math.max(1, concurrency | 0) }, runOne);
  await Promise.all(runners);
  return results;
}

// Groups import row problems by reason so a long file reads as a few counted lines
// instead of the first handful of rows. problems: [{ rowNum, kind: "skip" | "note", reason, detail }]
export function formatImportProblems(problems, samples = 4) {
  const sections = [
    ["skip", "Skipped"],
    ["note", "Imported, with a note"],
  ];
  const out = [];

  for (const [kind, title] of sections) {
    const byReason = new Map();
    for (const p of problems || []) {
      if (p.kind !== kind) continue;
      if (!byReason.has(p.reason)) byReason.set(p.reason, []);
      byReason.get(p.reason).push(p);
    }
    if (!byReason.size) continue;

    const total = [...byReason.values()].reduce((n, list) => n + list.length, 0);
    if (out.length) out.push("");
    out.push(`${title} (${total}):`);

    // Biggest groups first
    for (const [reason, list] of [...byReason].sort((a, b) => b[1].length - a[1].length)) {
      out.push(`• ${reason} — ${list.length}`);
      for (const p of list.slice(0, samples)) {
        out.push(`    Row ${p.rowNum}${p.detail ? `: ${p.detail}` : ""}`);
      }
      if (list.length > samples) out.push(`    …and ${list.length - samples} more`);
    }
  }

  return out.join("\n");
}
